import { useEffect, useMemo, useState } from "react";
import {
  IFavoriteOriginWithMonths,
  ModeFilter,
  OriginTypeFilter,
} from "../../../../types/IFavoriteOrigins";
import { ProductSectionOrder } from "../../../../types/IOriginManager";
import {
  getDefaultProductSectionOrder,
  getRowKey,
  mergeSectionOrder,
  sortFavoriteOriginsByOrder,
  sortRowsBySectionOrder,
} from "../FavoriteOriginHelper";

interface UseOriginManagerRowsParams {
  kochOrigins: IFavoriteOriginWithMonths[];
  competitorOrigins: IFavoriteOriginWithMonths[];
  selectedProduct: string;
  modeFilter: ModeFilter;
  originTypeFilter: OriginTypeFilter;
  originFilterText: string;
  editingNewlyAddedIds: Set<string>;
  sectionOrderByProduct: Record<string, ProductSectionOrder>;
  setSectionOrderByProduct: React.Dispatch<
    React.SetStateAction<Record<string, ProductSectionOrder>>
  >;
}

export interface UseOriginManagerRowsResult {
  savedOrigins: IFavoriteOriginWithMonths[];
  newlyAddedOrigins: Record<string, IFavoriteOriginWithMonths[]>;
  setNewlyAddedOriginsByProduct: React.Dispatch<
    React.SetStateAction<Record<string, IFavoriteOriginWithMonths[]>>
  >;
  deletedRowKeysByProduct: Record<string, Set<string>>;
  setDeletedRowKeysByProduct: React.Dispatch<
    React.SetStateAction<Record<string, Set<string>>>
  >;
  rows: IFavoriteOriginWithMonths[];
  filteredRows: IFavoriteOriginWithMonths[];
}

const hasOutboundMode = (
  origin: IFavoriteOriginWithMonths,
  mode: "truck" | "rail",
) =>
  origin.location.outboundModes
    .map((m) => m.trim().toUpperCase())
    .includes(mode.toUpperCase());

// Combines server-saved origins with unsaved local additions/deletions into the
// filtered, section-ordered row list the table renders.
export const useOriginManagerRows = ({
  kochOrigins,
  competitorOrigins,
  selectedProduct,
  modeFilter,
  originTypeFilter,
  originFilterText,
  editingNewlyAddedIds,
  sectionOrderByProduct,
  setSectionOrderByProduct,
}: UseOriginManagerRowsParams): UseOriginManagerRowsResult => {
  // local storage for newly added origins that are not saved to the server yet by product, so that switching products doesn't lose the unsaved additions
  const [newlyAddedOrigins, setNewlyAddedOriginsByProduct] = useState<
    Record<string, IFavoriteOriginWithMonths[]>
  >({});
  // local storage for origins that have been deleted but not yet saved to the server, so that switching products doesn't lose the unsaved deletions
  const [deletedRowKeysByProduct, setDeletedRowKeysByProduct] = useState<
    Record<string, Set<string>>
  >({});

  // Combine koch and competitor origins from the server into a single sorted array.
  const savedOrigins = useMemo<IFavoriteOriginWithMonths[]>(() => {
    const sortedKoch = sortFavoriteOriginsByOrder(kochOrigins);
    const sortedCompetitor = sortFavoriteOriginsByOrder(competitorOrigins);

    return [...sortedKoch, ...sortedCompetitor];
  }, [kochOrigins, competitorOrigins]);

  const rows = useMemo(() => {
    const newOriginsByProduct = newlyAddedOrigins[selectedProduct] ?? [];
    const deletedRowKeys =
      deletedRowKeysByProduct[selectedProduct] ?? new Set<string>();
    const matchesModeFilter = (origin: IFavoriteOriginWithMonths) => {
      if (modeFilter === "truck") {
        return hasOutboundMode(origin, "truck");
      }
      if (modeFilter === "rail") {
        return hasOutboundMode(origin, "rail");
      }
      return true;
    };
    const byType = (origin: IFavoriteOriginWithMonths) =>
      (originTypeFilter === "all" ||
        (originTypeFilter === "koch" && !origin.competitor) ||
        (originTypeFilter === "competitor" && origin.competitor)) &&
      matchesModeFilter(origin) &&
      !deletedRowKeys.has(
        `${origin.competitor ? "competitor" : "koch"}-${origin.location.id}`,
      );
    const filteredNewRows = newOriginsByProduct.filter(byType);
    const filteredExistingRows = savedOrigins.filter(byType);

    // Split new rows into those currently in editing mode vs already saved.
    const editingRows = filteredNewRows.filter((o) =>
      editingNewlyAddedIds.has(o.location.id),
    );
    const savedNewRows = filteredNewRows.filter(
      (o) => !editingNewlyAddedIds.has(o.location.id),
    );

    // Build the non-editing pool: existing rows, overridden by saved-new rows
    // (so locally updated prices take precedence over stale server values).
    // Also remove any origin that is currently in editing mode to avoid duplicates.
    const editingKeys = new Set(
      editingRows.map(
        (o) => `${o.competitor ? "competitor" : "koch"}-${o.location.id}`,
      ),
    );
    const restMap = new Map<string, IFavoriteOriginWithMonths>();
    filteredExistingRows.forEach((origin) => {
      const key = `${origin.competitor ? "competitor" : "koch"}-${
        origin.location.id
      }`;
      if (!editingKeys.has(key)) {
        restMap.set(key, origin);
      }
    });
    savedNewRows.forEach((origin) => {
      const key = `${origin.competitor ? "competitor" : "koch"}-${
        origin.location.id
      }`;
      restMap.set(key, origin);
    });

    // Keep Koch guidance rows above competitor rows, while preserving order within each group.
    const restRows = Array.from(restMap.values());
    const editingKochRows = editingRows.filter((row) => !row.competitor);
    const editingCompetitorRows = editingRows.filter((row) => row.competitor);
    const restKochRows = restRows.filter((row) => !row.competitor);
    const restCompetitorRows = restRows.filter((row) => row.competitor);

    const productOrder =
      sectionOrderByProduct[selectedProduct] ?? getDefaultProductSectionOrder();

    const kochRows = sortRowsBySectionOrder(
      [...editingKochRows, ...restKochRows],
      productOrder.koch,
    );
    const competitorRows = sortRowsBySectionOrder(
      [...editingCompetitorRows, ...restCompetitorRows],
      productOrder.competitor,
    );

    return [...kochRows, ...competitorRows];
  }, [
    savedOrigins,
    deletedRowKeysByProduct,
    newlyAddedOrigins,
    originTypeFilter,
    modeFilter,
    selectedProduct,
    editingNewlyAddedIds,
    sectionOrderByProduct,
  ]);

  const filteredRows = useMemo(() => {
    const query = originFilterText.trim().toLowerCase();
    if (!query) {
      return rows;
    }

    return rows.filter((origin) =>
      origin.location.name.toLowerCase().includes(query),
    );
  }, [originFilterText, rows]);

  // Keep each product's section order in sync as rows are added/removed.
  useEffect(() => {
    const currentRows = [
      ...(newlyAddedOrigins[selectedProduct] ?? []),
      ...savedOrigins,
    ];
    const deletedKeys =
      deletedRowKeysByProduct[selectedProduct] ?? new Set<string>();

    const visibleRows = currentRows.filter(
      (origin) => !deletedKeys.has(getRowKey(origin)),
    );
    const kochKeys = visibleRows
      .filter((origin) => !origin.competitor)
      .map(getRowKey);
    const competitorKeys = visibleRows
      .filter((origin) => origin.competitor)
      .map(getRowKey);

    setSectionOrderByProduct((prev) => {
      const existing = prev[selectedProduct] ?? getDefaultProductSectionOrder();
      const next: ProductSectionOrder = {
        koch: mergeSectionOrder(existing.koch, kochKeys),
        competitor: mergeSectionOrder(existing.competitor, competitorKeys),
      };

      if (
        next.koch.join("|") === existing.koch.join("|") &&
        next.competitor.join("|") === existing.competitor.join("|")
      ) {
        return prev;
      }

      return {
        ...prev,
        [selectedProduct]: next,
      };
    });
  }, [
    savedOrigins,
    deletedRowKeysByProduct,
    newlyAddedOrigins,
    selectedProduct,
    setSectionOrderByProduct,
  ]);

  return {
    savedOrigins,
    newlyAddedOrigins,
    setNewlyAddedOriginsByProduct,
    deletedRowKeysByProduct,
    setDeletedRowKeysByProduct,
    rows,
    filteredRows,
  };
};
